import { Modal, Pressable, Text, View, type StyleProp, type ViewStyle } from 'react-native';
import { twMerge } from 'tailwind-merge';

interface BottomSheetProps {
  visible: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  /** 'bottom' slides up from bottom; 'centered' renders as a centered dialog */
  variant?: 'bottom' | 'centered';
  contentStyle?: StyleProp<ViewStyle>;
}

export function BottomSheet({
  visible,
  onClose,
  title,
  children,
  variant = 'bottom',
  contentStyle,
}: BottomSheetProps) {
  const isBottom = variant === 'bottom';
  const hasCustomSize = contentStyle != null;

  return (
    <Modal
      visible={visible}
      transparent
      animationType={isBottom ? 'slide' : 'fade'}
      onRequestClose={onClose}
    >
      <View
        className={twMerge(
          'flex-1 bg-black/40',
          isBottom ? 'justify-end' : 'justify-center p-5',
        )}
      >
        <Pressable className="absolute inset-0" onPress={onClose} />
        <View
          className={twMerge(
            'bg-white dark:bg-gray-800 overflow-hidden',
            isBottom
              ? `rounded-t-2xl pb-8 ${hasCustomSize ? '' : 'max-h-[60%]'}`
              : `rounded-2xl ${hasCustomSize ? '' : 'max-h-[80%]'}`,
          )}
          style={contentStyle}
        >
          {title && (
            <View className="flex-row items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
              <Text className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {title}
              </Text>
              <Pressable onPress={onClose} hitSlop={8}>
                <Text className="text-[15px] font-semibold text-forest-600 dark:text-forest-500">
                  Done
                </Text>
              </Pressable>
            </View>
          )}
          {children}
        </View>
      </View>
    </Modal>
  );
}
